import React, { createContext, useContext, useEffect, useState } from "react";
import { translations, LANGUAGES, RTL_LANGS } from "@/i18n/translations";

const AppContext = createContext(null);

// Prices are entered by the admin in USD and converted automatically.
// Static reference rates relative to 1 USD.
export const CURRENCIES = {
  USD: { symbol: "$", rate: 1, decimals: 2 },
  EUR: { symbol: "€", rate: 0.92, decimals: 2 },
  GBP: { symbol: "£", rate: 0.79, decimals: 2 },
  JPY: { symbol: "¥", rate: 157, decimals: 0 },
  CNY: { symbol: "CN¥", rate: 7.25, decimals: 2 },
  CHF: { symbol: "CHF ", rate: 0.89, decimals: 2 },
  CAD: { symbol: "C$", rate: 1.36, decimals: 2 },
  AUD: { symbol: "A$", rate: 1.52, decimals: 2 },
  INR: { symbol: "₹", rate: 83.3, decimals: 2 },
  HKD: { symbol: "HK$", rate: 7.81, decimals: 2 },
  SGD: { symbol: "S$", rate: 1.35, decimals: 2 },
  KRW: { symbol: "₩", rate: 1350, decimals: 0 },
  BRL: { symbol: "R$", rate: 5.4, decimals: 2 },
  MXN: { symbol: "Mex$", rate: 18.0, decimals: 2 },
  SEK: { symbol: "kr ", rate: 10.6, decimals: 2 },
};

function detectLanguage() {
  const saved = localStorage.getItem("pv_lang");
  if (saved && translations[saved]) return saved;
  const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
  return translations[nav] ? nav : "en";
}

export function AppProvider({ children }) {
  const [lang, setLang] = useState(detectLanguage);
  const [currency, setCurrency] = useState(localStorage.getItem("pv_currency") || "USD");
  const [theme, setTheme] = useState(() => localStorage.getItem("pv_theme") || "light");
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    localStorage.setItem("pv_lang", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL_LANGS.includes(lang) ? "rtl" : "ltr";
    document.title = `andywarhol.ai — ${translations[lang]?.tagline ?? translations.en.tagline}`;
  }, [lang]);
  useEffect(() => localStorage.setItem("pv_currency", currency), [currency]);
  useEffect(() => {
    localStorage.setItem("pv_theme", theme);
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  const t = (key) => translations[lang]?.[key] ?? translations.en[key] ?? key;
  const toggleTheme = () => setTheme((v) => (v === "dark" ? "light" : "dark"));

  const formatPrice = (usd) => {
    const c = CURRENCIES[currency] || CURRENCIES.USD;
    const value = Number(usd) * c.rate;
    const formatted = value.toLocaleString(undefined, {
      minimumFractionDigits: c.decimals,
      maximumFractionDigits: c.decimals,
    });
    return `${c.symbol}${formatted}`;
  };

  return (
    <AppContext.Provider value={{
      lang, setLang, currency, setCurrency, t, formatPrice,
      languages: LANGUAGES, currencies: Object.keys(CURRENCIES),
      theme, toggleTheme, admin, setAdmin,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
